import { HttpErrorResponse } from '@angular/common/http';
import { ClientTranslatableException, NestedError } from '@softline/core';
import {
  CONSTRUCTION_SITE_ERROR_CREATE_EXCEPTION,
  CONSTRUCTION_SITE_ERROR_CUSTOMER_NOT_FOUND,
  CONSTRUCTION_SITE_ERROR_EDIT_NOT_ALLOWED,
  CONSTRUCTION_SITE_ERROR_NOT_A_SUBCUSTOMER,
  CONSTRUCTION_SITE_ERROR_NOT_FOUND,
  CONSTRUCTION_SITE_ERROR_UPDATE_EXCEPTION,
  ConstructionSiteError,
} from '../types/construction-site';

function isConstructionSiteError(body: unknown): body is ConstructionSiteError {
  return (
    !!body &&
    typeof body === 'object' &&
    typeof (body as ConstructionSiteError).key === 'string' &&
    !!(body as ConstructionSiteError).key
  );
}

/**
 * Pulls the `{ key, params? }` body out of a failed construction-site request.
 * Walks the whole error chain because the framework only maps the body onto
 * {@link ClientTranslatableException} when `params` is present — these endpoints
 * may omit it, in which case the body survives only on the wrapped
 * `HttpErrorResponse`.
 */
export function extractConstructionSiteError(
  error: unknown
): ConstructionSiteError | null {
  const seen = new Set<unknown>();
  let current: unknown = error;
  while (current && !seen.has(current)) {
    seen.add(current);
    if (current instanceof ClientTranslatableException && isConstructionSiteError(current.cte))
      return current.cte;
    const body = current instanceof HttpErrorResponse ? current.error : current;
    if (isConstructionSiteError(body)) return body;
    current = current instanceof NestedError ? current.inner : undefined;
  }
  return null;
}

const MESSAGES: Record<string, string> = {
  [CONSTRUCTION_SITE_ERROR_CUSTOMER_NOT_FOUND]: 'Der Kunde ist nicht bekannt.',
  [CONSTRUCTION_SITE_ERROR_NOT_FOUND]: 'Die Baustelle existiert nicht.',
  [CONSTRUCTION_SITE_ERROR_NOT_A_SUBCUSTOMER]:
    'Die Baustelle gehört nicht zu diesem Kunden.',
  [CONSTRUCTION_SITE_ERROR_EDIT_NOT_ALLOWED]:
    'Baustellen dieses Kunden dürfen nicht geändert werden.',
  [CONSTRUCTION_SITE_ERROR_CREATE_EXCEPTION]:
    'Die Baustelle konnte nicht angelegt werden.',
  [CONSTRUCTION_SITE_ERROR_UPDATE_EXCEPTION]:
    'Die Baustelle konnte nicht geändert werden.',
};

const FALLBACK_CREATE = 'Die Baustelle konnte nicht angelegt werden.';
const FALLBACK_UPDATE = 'Die Baustelle konnte nicht geändert werden.';

/**
 * Message for a construction-site error. Beyond the six documented keys the
 * backend forwards arbitrary legacy-validation keys (missing/too short/unknown
 * country, PLZ, bank, representative) that cannot be enumerated — those fall back
 * to a generic message naming the raw key, so support can still identify them.
 */
export function constructionSiteErrorMessage(
  error: ConstructionSiteError | null,
  mode: 'create' | 'update'
): string {
  const fallback = mode === 'create' ? FALLBACK_CREATE : FALLBACK_UPDATE;
  if (!error) return fallback;
  const known = MESSAGES[error.key];
  if (known) return known;
  return `${fallback} (${error.key})`;
}

export function isEditNotAllowed(error: ConstructionSiteError | null): boolean {
  return error?.key === CONSTRUCTION_SITE_ERROR_EDIT_NOT_ALLOWED;
}
